import { useState, useEffect } from 'react';
import { Pencil, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { type LucideIcon } from 'lucide-react';

export interface CriteriaCardData {
  label: string;
  value: string;
  icon: LucideIcon;
}

interface CCCEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cards: CriteriaCardData[];
  onSave: (cards: CriteriaCardData[]) => void;
}

const STRATEGY_OPTIONS = [
  'Long Term Rental (Single Family)',
  'Long Term Rental (Multifamily)',
  'Short Term Rental',
  'Mid Term Rental',
  'Coliving',
  'Section 8',
  'Flipping/Wholesaling',
  'Other',
];

const STANDARD_LABELS = ['Strategy', 'Property Type', 'Location', 'Condition', 'Price Range', 'Profitability'];

const FIELD_PLACEHOLDERS: Record<string, string> = {
  'Property Type': 'e.g., Single-family homes',
  'Location': 'e.g., Phoenix suburbs, within 30 min of downtown',
  'Condition': 'e.g., Move-in ready or light cosmetic updates only',
  'Price Range': 'e.g., $150,000 – $300,000',
  'Profitability': 'e.g., Min. $300/month cash flow, 8% cap rate',
};

function initStrategyState(cards: CriteriaCardData[]) {
  const strategyCard = cards.find(c => c.label === 'Strategy');
  const sv = strategyCard?.value ?? '';
  const isOther = sv !== '' && !STRATEGY_OPTIONS.slice(0, -1).includes(sv);
  return {
    selectedOption: isOther ? 'Other' : sv,
    otherText: isOther ? sv : '',
  };
}

export default function CCCEditDialog({ open, onOpenChange, cards, onSave }: CCCEditDialogProps) {
  const standardCards = cards.filter(c => STANDARD_LABELS.includes(c.label));
  const notesCard = cards.find(c => c.label === 'Strategy Notes');

  const [editCards, setEditCards] = useState<CriteriaCardData[]>(standardCards);
  const [strategyOption, setStrategyOption] = useState(() => initStrategyState(standardCards).selectedOption);
  const [otherStrategyText, setOtherStrategyText] = useState(() => initStrategyState(standardCards).otherText);
  const [strategyNotes, setStrategyNotes] = useState(notesCard?.value ?? '');

  useEffect(() => {
    if (!open) return;
    const std = cards.filter(c => STANDARD_LABELS.includes(c.label));
    const notes = cards.find(c => c.label === 'Strategy Notes');
    setEditCards(std);
    setStrategyNotes(notes?.value ?? '');
    const { selectedOption, otherText } = initStrategyState(std);
    setStrategyOption(selectedOption);
    setOtherStrategyText(otherText);
  }, [open, cards]);

  const updateValue = (index: number, newValue: string) => {
    setEditCards(prev => prev.map((c, i) => (i === index ? { ...c, value: newValue } : c)));
  };

  const handleStrategySelect = (value: string) => {
    setStrategyOption(value);
    if (value === 'Other') {
      updateValue(0, otherStrategyText);
    } else {
      updateValue(0, value);
      setOtherStrategyText('');
    }
  };

  const handleOtherStrategyChange = (text: string) => {
    setOtherStrategyText(text);
    updateValue(0, text);
  };

  const handleSave = () => {
    const notesIcon = editCards[0]?.icon;
    const notesCardData: CriteriaCardData = {
      label: 'Strategy Notes',
      value: strategyNotes,
      icon: notesIcon,
    };
    onSave([...editCards, notesCardData]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Pencil className="h-4 w-4 text-gold" />
            Edit Crystal Clear Criteria
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {editCards.map((card, i) => {
            const Icon = card.icon;

            if (card.label === 'Strategy') {
              return (
                <div key={card.label} className="flex items-start gap-3">
                  <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-gold/15 flex items-center justify-center mt-0.5">
                    <Icon className="h-4 w-4 text-gold" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 block">
                        Strategy
                      </label>
                      <Select value={strategyOption} onValueChange={handleStrategySelect}>
                        <SelectTrigger className="bg-secondary border-border text-sm">
                          <SelectValue placeholder="Select your strategy..." />
                        </SelectTrigger>
                        <SelectContent>
                          {STRATEGY_OPTIONS.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {strategyOption === 'Other' && (
                        <Input
                          value={otherStrategyText}
                          onChange={(e) => handleOtherStrategyChange(e.target.value)}
                          placeholder="Describe your strategy..."
                          maxLength={80}
                          className="mt-2 bg-secondary border-border text-sm placeholder:text-muted-foreground/50 placeholder:italic"
                        />
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 block">
                        Additional Strategy Notes
                      </label>
                      <Textarea
                        value={strategyNotes}
                        onChange={(e) => setStrategyNotes(e.target.value)}
                        placeholder="Any additional context about your strategy..."
                        maxLength={80}
                        rows={3}
                        className="bg-secondary border-border text-sm resize-none placeholder:text-muted-foreground/50 placeholder:italic"
                      />
                      <span className="text-xs text-muted-foreground mt-0.5">{strategyNotes.length}/80</span>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={card.label} className="flex items-start gap-3">
                <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-gold/15 flex items-center justify-center mt-0.5">
                  <Icon className="h-4 w-4 text-gold" />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 block">
                    {card.label}
                  </label>
                  <Input
                    value={editCards[i].value.replace(/\n/g, ', ')}
                    onChange={(e) => updateValue(i, e.target.value)}
                    maxLength={80}
                    placeholder={FIELD_PLACEHOLDERS[card.label] ?? ''}
                    className="bg-secondary border-border text-sm placeholder:text-muted-foreground/50 placeholder:italic"
                  />
                  <span className="text-xs text-muted-foreground mt-0.5">{editCards[i].value.length}/80</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button onClick={handleSave} className="gradient-gold text-primary-foreground shadow-gold hover:opacity-90">
            <Save className="h-4 w-4 mr-1" /> Save Criteria
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
